import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cartApi, type AddToCartPayload } from '@/services/api/cart';
import { ApiError } from '@/services/api/client';
import { useUiStore } from '@/stores/ui.store';
import { useZoneStore } from '@/stores/zone.store';
import type { Cart } from '@/types/cart';

const CART_KEY = ['cart'] as const;

/**
 * Sepet SUNUCU verisidir: TanStack Query'de yaşar, Zustand'a kopyalanmaz.
 * Her mutation backend'in döndürdüğü güncel sepeti cache'e yazar —
 * böylece istemci tarafında ikinci bir "doğru" oluşmaz.
 */
export function useCart() {
  return useQuery({
    queryKey: CART_KEY,
    queryFn: cartApi.get,
    staleTime: 30_000,
  });
}

function useCartMutation<TArgs>(
  mutationFn: (args: TArgs) => Promise<Cart>,
  options: { successMessage?: (cart: Cart, args: TArgs) => string } = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (cart, args) => {
      queryClient.setQueryData(CART_KEY, cart);

      if (options.successMessage) {
        toast.success(options.successMessage(cart, args));
      }
    },
    onError: (error) => {
      // Backend anlaşılır mesaj döner ("... tükendi", "minimum sepet ...").
      toast.error(
        error instanceof ApiError ? error.message : 'İşlem tamamlanamadı.',
      );
    },
  });
}

export function useAddToCart() {
  const openOverlay = useUiStore((state) => state.openOverlay);

  const mutation = useCartMutation<AddToCartPayload>((payload) =>
    cartApi.addItem(payload),
  );

  return {
    ...mutation,
    addToCart: (payload: AddToCartPayload) =>
      mutation.mutate(payload, {
        onSuccess: () => openOverlay('cart'),
      }),
  };
}

export function useUpdateCartItem() {
  return useCartMutation<{
    itemId: string;
    quantity?: number;
    addons?: Array<{ addonId: string; quantity: number }>;
  }>(({ itemId, ...payload }) => cartApi.updateItem(itemId, payload));
}

export function useRemoveCartItem() {
  return useCartMutation<string>((itemId) => cartApi.removeItem(itemId));
}

/**
 * Bölge seçimi hem sepete (fiyat için) hem de yerel store'a (vitrin
 * göstergeleri için) yazılır — iki yüzey ayrışmasın.
 */
export function useSetCartZone() {
  const setZone = useZoneStore((state) => state.setZone);

  const mutation = useCartMutation<string>((zone) => cartApi.setZone(zone));

  return {
    ...mutation,
    setCartZone: (zoneSlug: string) =>
      mutation.mutate(zoneSlug, {
        onSuccess: () => setZone(zoneSlug),
      }),
  };
}

export function useApplyCoupon() {
  return useCartMutation<string>((code) => cartApi.applyCoupon(code), {
    successMessage: () => 'Kupon uygulandı.',
  });
}

export function useRemoveCoupon() {
  return useCartMutation<void>(() => cartApi.removeCoupon());
}

export function useCartItemCount(): number {
  const { data } = useCart();
  return data?.itemCount ?? 0;
}
